import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { bookingService } from '../../services/bookingService';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BackButton from '../../components/BackButton';
import { formatNumber } from '../../utils/formatNumber';
import { getPhotographyTypeLabelAr } from '../../utils/bookingLabels';
import { formatTimeToArabic } from '../../utils/formatDate';

const PhotographerConfirmation = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    if (bookingId) {
      loadBooking();
      startTimer();
    }
  }, [bookingId, user]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      const data = await bookingService.getBookingById(bookingId);
      setBooking(data);

      // Verify photographer is assigned
      if (data?.assignedPhotographerId !== user?.id) {
        alert('هذا الحجز غير مخصص لك');
        navigate('/photographer-dashboard');
        return;
      }

      // Verify booking status
      if (data?.status !== 'pending_photographer_confirmation') {
        if (data?.status === 'awaiting_payment') {
          alert('تم قبول الحجز بالفعل. في انتظار دفع العميل.');
        } else if (data?.status === 'paid_confirmed') {
          alert('تم تأكيد الحجز ودفعه بالفعل.');
        }
        navigate('/photographer-dashboard');
        return;
      }

      // Calculate time remaining
      if (data?.holdExpiresAt) {
        updateTimeRemaining(data.holdExpiresAt);
      }
    } catch (error) {
      console.error('Failed to load booking:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTimeRemaining = (expiresAt) => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diff = expiry - now;

    if (diff <= 0) {
      setTimeRemaining({ minutes: 0, seconds: 0, expired: true });
      return;
    }

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    setTimeRemaining({ minutes, seconds, expired: false });
  };

  const startTimer = () => {
    const interval = setInterval(() => {
      if (booking?.holdExpiresAt) {
        updateTimeRemaining(booking.holdExpiresAt);
      }
    }, 1000);

    return () => clearInterval(interval);
  };

  const handleAccept = async () => {
    if (!window.confirm('هل أنت متأكد من قبول هذا الحجز؟ سيتم إشعار العميل للدفع.')) {
      return;
    }

    try {
      setProcessing(true);
      await bookingService.acceptBooking(bookingId);
      
      // Show success and redirect
      alert('تم قبول الحجز بنجاح! سيتم إشعار العميل للدفع.');
      navigate('/photographer-dashboard');
    } catch (error) {
      console.error('Failed to accept booking:', error);
      alert('فشل قبول الحجز. يرجى المحاولة مرة أخرى.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async () => {
    if (!window.confirm('هل أنت متأكد من رفض هذا الحجز؟ سيتم البحث عن مصور آخر للعميل.')) {
      return;
    }

    try {
      setProcessing(true);
      await bookingService.declineBooking(bookingId);
      
      alert('تم رفض الحجز. سيتم البحث عن مصور آخر للعميل.');
      navigate('/photographer-dashboard');
    } catch (error) {
      console.error('Failed to decline booking:', error);
      alert('فشل رفض الحجز. يرجى المحاولة مرة أخرى.');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex items-center justify-center" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  if (!booking || booking.assignedPhotographerId !== user?.id) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50 flex flex-col items-center justify-center p-4" dir="rtl">
        <div className="text-center">
          <Icon name="AlertCircle" size={64} className="mx-auto mb-4 text-error" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">الحجز غير موجود أو غير مخصص لك</h2>
          <BackButton to="/photographer-dashboard" label="العودة إلى لوحة التحكم" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-slate-50" dir="rtl">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-12">
        <div className="max-w-3xl mx-auto">
          <div className="mb-4">
            <BackButton to="/photographer-dashboard" label="العودة إلى لوحة التحكم" />
          </div>
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8 md:p-12">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="Calendar" size={32} className="text-primary" />
              </div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">طلب حجز جديد</h1>
              <p className="text-slate-600">
                يرجى تأكيد توفرك خلال{' '}
                {timeRemaining && !timeRemaining.expired && (
                  <span className="font-semibold text-primary">
                    {timeRemaining.minutes} دقيقة و {timeRemaining.seconds} ثانية
                  </span>
                )}
              </p>
            </div>

            {/* Customer Info */}
            <div className="bg-slate-50 rounded-xl p-6 mb-6">
              <h3 className="font-semibold text-slate-900 mb-4">معلومات العميل</h3>
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  {booking.customer?.avatar_url || booking.customer?.avatarUrl ? (
                    <img
                      src={booking.customer.avatar_url || booking.customer.avatarUrl}
                      alt={booking.customer.full_name || booking.customer.fullName}
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={32} className="text-primary" />
                  )}
                </div>
                <div className="text-right">
                  <p className="font-semibold text-slate-900">
                    {booking.customer?.full_name || booking.customer?.fullName || 'عميل'}
                  </p>
                  <p className="text-sm text-slate-600">
                    {booking.customer?.phone || 'لا يوجد رقم هاتف'}
                  </p>
                </div>
              </div>
            </div>

            {/* Booking Details */}
            <div className="space-y-6 mb-8">
              <div>
                <h3 className="font-semibold text-slate-900 mb-4">تفاصيل الحجز</h3>
                <div className="bg-slate-50 rounded-xl p-6 space-y-4 text-right">
                  <div className="flex justify-between">
                    <span className="text-slate-600">نوع الخدمة</span>
                    <span className="font-medium text-slate-900">
                      {getPhotographyTypeLabelAr(booking.photographyType || booking.serviceType)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">المدينة</span>
                    <span className="font-medium text-slate-900">{booking.city || 'الرياض'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">نوع الموقع</span>
                    <span className="font-medium text-slate-900">
                      {booking.locationType || 'غير محدد'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">التاريخ</span>
                    <span className="font-medium text-slate-900">
                      {booking.bookingDate 
                        ? new Date(booking.bookingDate).toLocaleDateString('ar-SA-u-ca-gregory-nu-latn', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                        : 'غير محدد'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">الوقت</span>
                    <span className="font-medium text-slate-900">
                      {formatTimeToArabic(booking.bookingTime)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">المدة</span>
                    <span className="font-medium text-slate-900">
                      {booking.duration || 'غير محدد'}
                    </span>
                  </div>
                  {booking.equipment && booking.equipment.length > 0 && (
                    <div>
                      <span className="text-slate-600 block mb-2">المعدات المطلوبة</span>
                      <div className="flex flex-wrap gap-2">
                        {booking.equipment.map((eq, index) => (
                          <span key={index} className="px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm">
                            {eq}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {booking.notes && (
                    <div>
                      <span className="text-slate-600 block mb-2">ملاحظات العميل</span>
                      <p className="text-slate-900 bg-white p-3 rounded-lg">{booking.notes}</p>
                    </div>
                  )}
                  <div className="border-t-2 border-slate-300 pt-4 flex justify-between">
                    <span className="text-slate-600 font-semibold">المبلغ</span>
                    <span className="font-bold text-primary text-2xl">
                      {formatNumber(booking.finalPrice || booking.price || 0)} ر.س
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-4">
              <Button
                variant="default"
                size="lg"
                fullWidth
                onClick={handleAccept}
                loading={processing}
                disabled={timeRemaining?.expired}
                iconName="CheckCircle"
                iconPosition="right"
                className="bg-success hover:bg-success/90"
              >
                متاح - قبول الحجز
              </Button>

              <Button
                variant="outline"
                size="lg"
                fullWidth
                onClick={handleDecline}
                loading={processing}
                disabled={timeRemaining?.expired}
                iconName="XCircle"
                iconPosition="right"
                className="border-error text-error hover:bg-error/10"
              >
                غير متاح - رفض الحجز
              </Button>

              {timeRemaining?.expired && (
                <div className="bg-error/10 border border-error/20 rounded-lg p-4 text-center">
                  <p className="text-sm text-error font-semibold">
                    انتهت مدة الاستجابة. تم إعادة تعيين الحجز تلقائياً.
                  </p>
                </div>
              )}

              <Button
                variant="ghost"
                onClick={() => navigate('/photographer-dashboard')}
                iconName="ArrowRight"
                fullWidth
              >
                العودة إلى لوحة التحكم
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotographerConfirmation;
